import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { characterMap } from '../assets/characters';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import { getOrCreateSessionToken } from '../utils/sessionManager';
import { logger } from '../utils/logger';
import { useLocalizedNavigate } from '../i18n/hooks';
import RecommendedSection from './RecommendedSection';

interface JoinRoomData {
  roomCode: string;
  playerName: string;
  password: string;
  character?: string;
}

const JoinRoom: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('join.pageTitle'));
  const navigate = useLocalizedNavigate();
  const [formData, setFormData] = useState<JoinRoomData>({
    roomCode: '',
    playerName: localStorage.getItem('playerName') || '',
    password: ''
  });
  const [selectedCharacter, setSelectedCharacter] = useState<string>(localStorage.getItem('playerCharacter') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState<any>(null);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [publicRooms, setPublicRooms] = useState<any[]>([]);
  const [loadingPublicRooms, setLoadingPublicRooms] = useState(false);
  const [roomsError, setRoomsError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'roomCode' ? value.toUpperCase() : value
    }));
  };

  const checkRoomCode = async () => {
    if (formData.roomCode.length !== 6) return;
    
    try {
      const response = await apiClient.get(`/api/rooms/${formData.roomCode}`);
      setRoomInfo(response.data);
      setShowPasswordField(response.data.hasPassword);
      setError('');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(t('join.errors.roomNotFoundWithHint'));
      } else {
        setError(t('join.errors.checkRoomFailed'));
      }
      setRoomInfo(null);
      setShowPasswordField(false);
    }
  };

  React.useEffect(() => {
    if (formData.roomCode.length === 6) {
      checkRoomCode();
    } else {
      setRoomInfo(null);
      setShowPasswordField(false);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.roomCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Generar sessionToken usando el nuevo sistema persistente
      const sessionToken = getOrCreateSessionToken(formData.roomCode, formData.playerName);
      
      const requestData = {
        roomCode: formData.roomCode,
        playerName: formData.playerName,
        password: formData.password,
        character: selectedCharacter,
        sessionId: sessionToken // Usar sessionToken en lugar de sessionId antiguo
      };

      await apiClient.post(`/api/rooms/${formData.roomCode}/join`, requestData);
      
      // Guardar información del jugador (ya no usar sessionStorage)
      localStorage.setItem('playerName', formData.playerName);
      if (selectedCharacter) localStorage.setItem('playerCharacter', selectedCharacter);
      
      // Redirigir a la sala
      navigate(`/room/${formData.roomCode}`);
    } catch (err: any) {
      logger.error('Error joining room:', err);
      if (err.response?.status === 403) {
        setError(t('join.errors.wrongPassword'));
      } else if (err.response?.status === 404) {
        setError(t('join.errors.roomNotFound'));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('join.errors.joinFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para unirse mediante URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomCodeFromUrl = urlParams.get('code');
    if (roomCodeFromUrl) {
      setFormData(prev => ({ ...prev, roomCode: roomCodeFromUrl.toUpperCase() }));
    }
  }, []);

  // Obtener lista de salas públicas activas (sin contraseña)
  const fetchPublicRooms = async () => {
    setLoadingPublicRooms(true);
    setRoomsError('');
    try {
      // Usar el nuevo endpoint optimizado /api/rooms/public
      const resp = await apiClient.get('/api/rooms/public');
      const rooms: any[] = Array.isArray(resp.data) ? resp.data : [];
      setPublicRooms(rooms);
    } catch (err: any) {
      setRoomsError(t('join.errors.publicRoomsFailed'));
      setPublicRooms([]);
    } finally {
      setLoadingPublicRooms(false);
    }
  };

  React.useEffect(() => {
    // Cargar al montar y refrescar cada 30s (reducido de 12s para optimizar)
    fetchPublicRooms();
    const intervalId = setInterval(fetchPublicRooms, 30000);
    
    // Pausar polling cuando la página no está visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(intervalId);
      } else {
        fetchPublicRooms();
        const newIntervalId = setInterval(fetchPublicRooms, 30000);
        return () => clearInterval(newIntervalId);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoinFromList = (code: string) => {
    setFormData(prev => ({ ...prev, roomCode: code }));
    // El efecto que escucha formData.roomCode realizará la validación
    // Desplazar hacia el formulario para visibilidad
    const el = document.querySelector('form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <>
      <SEOMeta
        title={t('join.seo.title')}
        description={t('join.seo.description')}
        canonical="https://el-impostor.com/join"
      />
      <div className="min-h-screen flex items-center justify-center p-4 bg-blue-50 dark:bg-gray-950">
      <div className="max-w-md w-full">
        <RecommendedSection position="top" />
        <div className="text-center mb-6">
          <button 
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-200 dark:hover:text-primary-100 mb-4 inline-flex items-center text-sm"
          >
            {t('join.backHome')}
          </button>
          <h1 className="text-3xl font-bold text-primary-800 dark:text-primary-200 mb-2">
            {t('join.header.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {t('join.header.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('join.form.roomCodeLabel')}
            </label>
            <input
              type="text"
              name="roomCode"
              value={formData.roomCode}
              onChange={handleInputChange}
              className="input-field text-center text-2xl font-mono tracking-widest"
              placeholder="ABC123"
              maxLength={6}
              style={{ textTransform: 'uppercase' }}
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('join.form.roomCodeHelp')}
            </p>
          </div>

          {roomInfo && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-green-800 dark:text-green-200">
                  {t('join.roomInfo.found')}
                </h3>
                <span className="text-sm text-green-600 dark:text-green-200">
                  {t('join.roomInfo.playersCount', { current: roomInfo.playerCount, max: roomInfo.maxPlayers })}
                </span>
              </div>
              <div className="text-sm text-green-700 dark:text-green-100">
                <p>{t('join.roomInfo.createdBy')} <span className="font-medium">{roomInfo.creatorName}</span></p>
                <p>
                  {t('join.roomInfo.statusLabel')}{' '}
                  <span className="font-medium">
                    {roomInfo.status === 'WAITING' ? t('join.roomInfo.statusWaiting') : t('join.roomInfo.statusPlaying')}
                  </span>
                </p>
                {roomInfo.hasPassword && (
                  <p className="text-orange-600 dark:text-orange-200">{t('join.roomInfo.requiresPassword')}</p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('join.form.nameLabel')}
            </label>
            <input
              type="text"
              name="playerName"
              value={formData.playerName}
              onChange={handleInputChange}
              className="input-field"
              placeholder={t('join.form.namePlaceholder')}
              minLength={2}
              maxLength={20}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('join.form.characterLabel')}
            </label>
            <div className="grid grid-cols-5 gap-2">
              {Object.keys(characterMap).map((id) => (
                <label key={id} className={`p-1 rounded cursor-pointer border ${selectedCharacter === id ? 'ring-2 ring-primary-500' : 'border-transparent'}`}>
                  <input type="radio" name="character" value={id} checked={selectedCharacter === id} onChange={() => setSelectedCharacter(id)} className="sr-only" />
                  <img src={characterMap[id]} alt={id} className="w-12 h-12 object-cover rounded" loading="lazy" />
                </label>
              ))}
            </div>
          </div>

          {showPasswordField && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('join.form.passwordLabel')}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="input-field"
                placeholder={t('join.form.passwordPlaceholder')}
                required={showPasswordField}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !roomInfo || (roomInfo?.status === 'PLAYING')}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('join.submit.joining')}
              </span>
            ) : roomInfo?.status === 'PLAYING' ? (
              t('join.submit.playingInProgress')
            ) : (
              t('join.submit.joinRoom')
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('join.noCode.prefix')}{' '}
            <button 
              onClick={() => navigate('/create')}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-200 dark:hover:text-primary-100 ml-1"
            >
              {t('join.noCode.createNew')}
            </button>
          </p>
        </div>
        {/* Lista de salas públicas (sin contraseña) */}
        <div className="mt-6 card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-primary-800 dark:text-primary-200 flex items-center gap-2">
              {t('join.publicRooms.title')}
              {publicRooms.length > 0 && (
                <span className="text-xs font-normal bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200 px-2 py-1 rounded-full">
                  {publicRooms.length}
                </span>
              )}
            </h2>
            <button 
              onClick={fetchPublicRooms} 
              disabled={loadingPublicRooms}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-200 dark:hover:text-primary-100 disabled:opacity-50 flex items-center gap-1"
            >
              {loadingPublicRooms ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                  {t('join.publicRooms.refreshing')}
                </>
              ) : (
                <>{t('join.publicRooms.refresh')}</>
              )}
            </button>
          </div>

          {roomsError && (
            <div className="text-sm text-red-600 dark:text-red-200 mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
              {roomsError}
            </div>
          )}

          {loadingPublicRooms && publicRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm">{t('join.publicRooms.loading')}</p>
            </div>
          ) : publicRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <div className="text-4xl mb-2">🏜️</div>
              <p className="text-sm">{t('join.publicRooms.emptyTitle')}</p>
              <p className="text-xs mt-1">{t('join.publicRooms.emptySubtitle')}</p>
            </div>
          ) : (
            <div className="grid gap-3 max-h-96 overflow-y-auto">
              {publicRooms.map((r) => {
                const occupancy = (r.playerCount / r.maxPlayers) * 100;
                const isAlmostFull = occupancy >= 75;
                const isMediumFull = occupancy >= 50 && occupancy < 75;
                
                // Calcular tiempo desde creación
                const createdAt = new Date(r.createdAt);
                const now = new Date();
                const diffMinutes = Math.floor((now.getTime() - createdAt.getTime()) / 60000);
                const timeAgo = diffMinutes < 1 ? t('join.publicRooms.timeAgo.justCreated') : 
                               diffMinutes === 1 ? t('join.publicRooms.timeAgo.oneMinute') : 
                               diffMinutes < 60 ? t('join.publicRooms.timeAgo.minutes', { minutes: diffMinutes }) : 
                               t('join.publicRooms.timeAgo.hours', { hours: Math.floor(diffMinutes / 60) });
                
                return (
                  <div 
                    key={r.code || r.roomCode} 
                    className="p-4 border-2 rounded-lg flex items-center justify-between bg-gradient-to-r from-white to-primary-50 dark:from-gray-900 dark:to-gray-950 hover:shadow-md transition-all duration-200 hover:border-primary-300 dark:border-gray-800"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xl font-bold tracking-widest text-primary-800">
                          {r.code || r.roomCode}
                        </span>
                        {diffMinutes < 5 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                            {t('join.publicRooms.newBadge')}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300 mb-2">
                        <span className="flex items-center gap-1">
                          👤 {r.creatorName || t('join.publicRooms.hostFallback')}
                        </span>
                        <span>•</span>
                        <span>{timeAgo}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-200">
                              {t('join.roomInfo.playersCount', { current: r.playerCount, max: r.maxPlayers })}
                            </span>
                            <span className={`font-semibold ${
                              isAlmostFull ? 'text-red-600' : 
                              isMediumFull ? 'text-yellow-600' : 
                              'text-green-600'
                            }`}>
                              {occupancy.toFixed(0)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 ${
                                isAlmostFull ? 'bg-red-500' : 
                                isMediumFull ? 'bg-yellow-500' : 
                                'bg-green-500'
                              }`}
                              style={{ width: `${occupancy}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <button 
                        className="px-4 py-2 text-sm font-semibold border-2 border-primary-600 rounded-lg text-primary-600 hover:bg-primary-600 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" 
                        onClick={() => handleJoinFromList((r.code || r.roomCode).toString())}
                        disabled={r.playerCount >= r.maxPlayers}
                      >
                        {r.playerCount >= r.maxPlayers ? t('join.publicRooms.full') : t('join.publicRooms.joinFromList')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
            {t('join.publicRooms.hint')}
          </div>
        </div>
        <RecommendedSection position="bottom" />
      </div>
    </div>
    </>
  );
};

export default JoinRoom;

